import * as React from 'react';
import { Component } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import Icon from './Icon';
import _FilterItem from './FilterItem';
import ClearFilters from './ClearFilters';
import { computed } from 'mobx';
import withFilter from './withFilter';
import HTag from './HTag';
import { isNullOrEmpty } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { getShadowRoot } from '../utils/shadowDomHelper';
import { FiltersProps } from '../models/components/Filter';

const FilterItem = withFilter(_FilterItem);

const FiltersMask = styled<any, any>('div', 'filtersMask', 'filters')`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    overflow: hidden;
    z-index: 15;
`;

const FiltersWrapper = styled<any, any>('aside', 'filtersWrapper', 'filters')`
    ${propStyles};
    position: absolute;
    top: 0;
    bottom: 0;
    transition: transform 200ms ease-in;
    transform: translate3d(-100%, 0, 0);
    &.open {
        transform: translate3d(0, 0, 0);
    }
    &.open:not(:focus-within) {
        bottom: 0.1px;
        transition: bottom 0.01s;
    }
`;

FiltersWrapper.defaultProps = {
    display: 'flex',
    flexDirection: 'column',
    zIndex: '15',
    width: '240px',
    bgColor: ['gray100', 'gray100', 'transparent'],
    fontSize: 'sm',
    borderWidth: '1px',
    borderColor: 'gray300',
    borderStyle: ['none solid none none', 'none solid none none', 'none'],
};

const FiltersWrapperInner = styled<any, any>(
    'div',
    'filtersWrapperInner',
    'filters'
)`
    ${propStyles};
`;

FiltersWrapperInner.defaultProps = {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
};

const FiltersHeader = styled<any, any>('div', 'filtersHeader', 'filters')`
    ${propStyles};
    flex-shrink: 0;
`;

FiltersHeader.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: 'body',
    bgColor: ['gray300', 'gray300', 'transparent'],
    margin: ['0', '0', '24px 0 0 0'],
    paddingY: 'md',
    paddingX: 'md',
};

const FiltersHeaderToggle = styled<any, any>(
    'button',
    'filtersHeaderToggle',
    'filters'
)`
    ${propStyles};
    outline: none;
    cursor: pointer;
`;

FiltersHeaderToggle.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    flex: '1 0 auto',
    fontFamily: 'heading',
    fontWeight: 'medium',
    fontSize: 'inherit',
    color: 'body',
    bgColor: 'transparent',
    borderStyle: 'none',
};

@inject('dataStore', 'filterStore', 'accessibilityStore', 'screenSizeStore')
@observer
class Filters extends Component<FiltersProps> {
    filtersHeaderToggleRef;
    constructor(props) {
        super(props);
        this.focusHeaderToggle = this.focusHeaderToggle.bind(this);
        this.filtersHeaderToggleRef = React.createRef();
    }

    componentDidMount(): void {
        const { filterStore } = this.props;
        // Ensure the expanded default state is set based on config and screensize
        filterStore.filtersOpenDefault();
    }

    componentDidUpdate(prevProps) {
        if (prevProps !== this.props) {
            const { filterStore } = this.props;
            filterStore.filtersOpenDefault();
        }
        const { dataStore } = this.props;
        // The use of the onTransitionEnd property on the element does not seem to work within Safari
        const filtersWrapper = getShadowRoot().querySelector('#FiltersWrapper');
        if (dataStore.isMobile && filtersWrapper) {
            filtersWrapper.addEventListener(
                'transitionend',
                this.focusHeaderToggle
            );
            // Ensure that the filter toggle gets focus on open
            this.focusHeaderToggle();
        }
    }

    componentWillUnmount() {
        const { dataStore } = this.props;
        const filtersWrapper = getShadowRoot().querySelector('#FiltersWrapper');
        if (dataStore.isMobile && filtersWrapper) {
            filtersWrapper.removeEventListener(
                'transitionend',
                this.focusHeaderToggle
            );
        }
    }

    // Filters are always open when above mobile size
    @computed
    get filtersOpen(): boolean {
        const { filterStore, dataStore } = this.props;
        // On desktop this cant expand
        return dataStore.isMobile ? filterStore.filtersOpen : true;
    }

    focusHeaderToggle() {
        if (this.filtersHeaderToggleRef) {
            this.filtersHeaderToggleRef.current.focus();
        }
    }

    @computed
    get filtersWrapper() {
        const { theme, filterStore, accessibilityStore } = this.props;
        return (
            <FiltersWrapper
                id={'FiltersWrapper'}
                className={'open'}
                {...theme.FiltersWrapper}
                role="complementary"
                aria-label={accessibilityStore.filtersLandmark}
            >
                <FiltersHeader {...theme.FiltersHeader}>
                    <FiltersHeaderToggle
                        onClick={filterStore.toggleFilters}
                        data-metric-id={'filterHeader-side-toggle'}
                        ref={this.filtersHeaderToggleRef}
                        id="FiltersHeaderToggle"
                        {...theme.FiltersHeaderToggle}
                    >
                        <Icon
                            fileName="FiltersHeaderToggleIcon"
                            display={['block', 'block', 'none']}
                            aria-hidden="true"
                            {...theme.FiltersHeaderToggleIcon}
                        />
                        <HTag
                            defaultStyleProps={{
                                margin: '0',
                                fontWeight: 'inherit',
                            }}
                            level={3}
                            text={'Filters'}
                        />
                    </FiltersHeaderToggle>
                    {filterStore.filtersActive && (
                        <ClearFilters fontSize="xs" />
                    )}
                </FiltersHeader>
                <FiltersWrapperInner {...theme.FiltersWrapperInner}>
                    {filterStore.filters.map(filter => (
                        <FilterItem key={filter.property} filter={filter} />
                    ))}
                </FiltersWrapperInner>
            </FiltersWrapper>
        );
    }

    render() {
        const { filterStore, screenSizeStore } = this.props;
        // Don't render the filters to the DOM if they aren't open.
        // This stops the screen reader from navigating through closed filters
        if (isNullOrEmpty(filterStore.filters) || !filterStore.filtersOpen) {
            return null;
        }
        return screenSizeStore.breakpointSize === 'sm' ? (
            <FiltersMask id={'filters-mask'}>{this.filtersWrapper}</FiltersMask>
        ) : (
            this.filtersWrapper
        );
    }
}

export default withTheme(Filters);



// WEBPACK FOOTER //
// ./src/Components/Filters.tsx